import React, { useEffect, useState } from 'react';
import './index.css';
import { Button, Dropdown, Image, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { format, isToday, isYesterday } from 'date-fns';
import { IoAnalyticsOutline, IoArrowBackOutline, IoBusiness, IoCalendarOutline, IoCallOutline, IoClose, IoDocumentTextOutline, IoLockClosed, IoLockOpen, IoMailOutline, IoPerson, IoTrashOutline } from 'react-icons/io5';
import noActivityFound from '../../../../assets/images/icons/activity.png';
import { db } from '../../../../firebase';
import { useAuth } from '../../../../context/AuthContext';
import OutletHead from '../../../../components/common/outlet-head';
import SelectDropdown from '../../../../components/common/utils/select-dropdown';
import { BsClock } from 'react-icons/bs';
import { IoMdAdd, IoMdMore } from 'react-icons/io';
import AddNote from '../../../../components/modals/lead/activities/notes/add-note';
import NoteComment from '../../../../components/modals/lead/activities/notes/comment';
import AddCall from '../../../../components/modals/lead/activities/calls/add-call';

function LeadDetails() {

  const { user, businessUsers } = useAuth();
  const { bid } = user.claims;

  const navigate = useNavigate();

  const { leadID } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [leadData, setLeadData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showAddCallModal, setShowAddCallModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [key, setKey] = useState("1");
  const [deleteID, setDeleteID] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const handleCloseDelete = () => setShowDelete(false);
  const handleShowDelete = () => setShowDelete(true);

  useEffect(() => {
    const leadDocRef = doc(db, "Businesses", bid, "Leads", leadID);
    const unsubscribeLead = onSnapshot(leadDocRef, (docSnapshot) => {
      setLeadData(docSnapshot.data());
    });

    const activitiesRef = collection(db, "Businesses", bid, "Leads", leadID, "Activities");
    const unsubscribeActivities = onSnapshot(activitiesRef, (snapshot) => {
      const tempData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setActivities(tempData);
    });

    return () => {
      unsubscribeLead();
      unsubscribeActivities();
      setIsLoading(false);
    };
  }, [leadID]);

  let textIcon = leadData?.name || "Lead Name";
  textIcon = textIcon.split(" ")[0][0] + textIcon.split(" ")[1][0];

  const getDisplayDate = (dateStr) => {
    const date = dateStr.toDate();
    if (isToday(date)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    return format(date, "d MMM yyyy");
  };

  const groupByRelativeDate = (activities) => {
    return activities.reduce((groups, activity) => {
      const key = getDisplayDate(activity.createdAt);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(activity);
      return groups;
    }, {});
  };

  async function handleDeleteActivity(id) {
    if (id) {
      await deleteDoc(doc(db, "Businesses", bid, "Leads", leadID, "Activities", id));
      handleCloseDelete();
      setDeleteID(null);
    }
  }

  const grouped = groupByRelativeDate(activities);

  return (
    <section className='section position-relative'>
      <OutletHead
        left={
          <div className='d-flex align-items-center'>
            <Button
              variant='light'
              size='sm'
              className='p-2 me-3 rounded-circle'
              onClick={() => navigate(-1)}
            >
              <IoArrowBackOutline size={24} />
            </Button>
            <h5 className='mb-0 fw-600'>Lead Details</h5>
          </div>
        }
        right={
          <div className='d-flex align-items-center'>
            <Button
              variant="outline-primary"
              type="submit"
              className='py-2 px-4'
            >
              <IoDocumentTextOutline size={18} />
              <span className='ps-2'>Export</span>
            </Button>
          </div>
        }
      />
      <div className='bg-light border-top'>
        <div className='row'>
          <div className='col-lg-12'>
            {
              isLoading ?
                <div className='data-loading-container'>
                  <div className='loader'>
                    <div className='spinner' />
                  </div>
                </div>
                :
                <div className='row'>
                  <div className='col-xl-4 inner-wrapper'>
                    <div className='lead-info-card bg-white ms-3 my-3 border rounded'>
                      <div className='lead-card-header rounded'>
                        <div className='lead-card-thumbnail'>
                          {textIcon}
                        </div>
                      </div>
                      <div className='lead-card-body pb-3 mt-5'>
                        <h5 className='text-center fw-600 text-capitalize pt-1'>{leadData?.name || "-"}</h5>
                        <p className='text-center text-muted mb-0'>{leadData ? `${leadData.address + ','} ${leadData.city}` : "-"}</p>
                        <p className='text-center text-muted mb-0'>{leadData ? `${leadData.state}` : "-"}</p>
                        <div className='d-flex align-items-center justify-content-center mb-4 mt-2'>
                          <div className='badge badge-dark-transparent text-dark fw-600 d-flex align-items-center me-1'>
                            {
                              leadData?.visibility === "Private" ?
                                <IoLockClosed size={10} />
                                :
                                <IoLockOpen size={10} />
                            }
                            <span className='ms-1'>{leadData?.visibility}</span>
                          </div>
                          <div className='badge btn-primary fw-600 d-flex align-items-center ms-1'>
                            {
                              leadData?.type === 'Person' ?
                                <IoPerson size={10} />
                                :
                                <IoBusiness size={10} />
                            }
                            <span className='ms-1'>{leadData?.type}</span>
                          </div>
                        </div>
                        <div className='border-top p-3'>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <small className='text-muted'>Date Created</small>
                            <span>{leadData?.createdAt ? format(new Date(leadData?.createdAt.seconds * 1000), "d MMM yyyy, h:mm a") : "-"}</span>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <small className='text-muted'>Phone Number</small>
                            <span>{leadData ? `+91 ${leadData.phoneNumber}` : "-"}</span>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <small className='text-muted'>Alternate Number</small>
                            <span>{leadData ? `+91 ${leadData.altPhoneNumber}` : "-"}</span>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <small className='text-muted'>Email</small>
                            <span>{leadData?.email || "-"}</span>
                          </div>
                        </div>
                        <div className='border-top p-3'>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <small className='text-muted'>Source</small>
                            <span className='badge badge-dark-transparent text-dark fw-normal px-3 py-2'>{leadData?.source || "-"}</span>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <small className='text-muted'>Property Type</small>
                            <span>{leadData?.propertyType || "-"}</span>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-0'>
                            <small className='text-muted'>Budget</small>
                            <span>{leadData?.budgetRange || "-"}</span>
                          </div>
                        </div>
                        <div className='border-top p-3'>
                          <h6 className='fw-600'>Interested Projects</h6>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <span>{leadData?.assignedTo || "[Projects Name]"}</span>
                          </div>
                        </div>
                        <div className='border-top p-3 pb-0'>
                          <h6 className='fw-600'>Assigned To</h6>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <span>{leadData?.assignedTo || "[Agent Name]"}</span>
                            <span>{leadData?.assignedTo || ""}</span>
                          </div>
                        </div>
                      </div>
                      <div className='lead-card-footer border-top'>
                        <div className='d-flex align-items-center justify-content-between mb-1'>
                          <small className='text-muted'>Last Updated</small>
                          <span>{leadData?.updatedAt ? format(new Date(leadData?.updatedAt.seconds * 1000), "d MMM yyyy, h:mm a") : "-"}</span>
                        </div>
                        <div className='d-flex align-items-center justify-content-between'>
                          <small className='text-muted'>Updated By</small>
                          <span>{businessUsers && leadData && businessUsers.find(x => x.id === leadData.createdBy)?.name || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-xl-8 inner-wrapper'>
                    <div className='bg-white me-3 my-3 border rounded'>
                      <div className='p-3 border-bottom'>
                        <h5 className='mb-0'>Lead Status</h5>
                      </div>

                      <div className='z-index-1099 d-flex align-items-center justify-content-center my-3 sticky-top'>
                        <div className='w-33 bg-white'>
                          <Button
                            variant='outline-light'
                            className={key === "1" ? 'rounded-0 fw-600 text-primary border-2 border-top-0 border-start-0 border-end-0 border-primary w-100' : 'rounded-0 fw-600 border-2 border-top-0 border-start-0 border-end-0 text-dark w-100'}
                            onClick={() => setKey("1")}
                          >
                            <IoAnalyticsOutline size={16} />
                            <div className='ms-1'>Activities</div>
                          </Button>
                        </div>
                        <div className='w-33 bg-white'>
                          <Button
                            variant='outline-light'
                            className={key === "2" ? 'rounded-0 fw-600 text-primary border-2 border-top-0 border-start-0 border-end-0 border-primary w-100' : 'rounded-0 fw-600 border-2 border-top-0 border-start-0 border-end-0 text-dark w-100'}
                            onClick={() => setKey("2")}
                          >
                            <IoDocumentTextOutline size={16} />
                            <div className='ms-1'>Notes</div>
                          </Button>
                        </div>
                        <div className='w-33 bg-white'>
                          <Button
                            variant='outline-light'
                            className={key === "3" ? 'rounded-0 fw-600 text-primary border-2 border-top-0 border-start-0 border-end-0 border-primary w-100' : 'rounded-0 fw-600 border-2 border-top-0 border-start-0 border-end-0 text-dark w-100'}
                            onClick={() => setKey("3")}
                          >
                            <IoCallOutline size={16} />
                            <div className='ms-1'>Calls</div>
                          </Button>
                        </div>
                        {/* <div className='w-25 bg-white'>
                          <Button
                            variant='outline-light'
                            className={key === "4" ? 'rounded-0 fw-600 text-primary border-2 border-top-0 border-start-0 border-end-0 border-primary w-100' : 'rounded-0 fw-600 border-2 border-top-0 border-start-0 border-end-0 text-dark w-100'}
                            onClick={() => setKey("4")}
                          >
                            <IoMailOutline size={16} />
                            <div className='ms-1'>Email</div>
                          </Button>
                        </div> */}
                      </div>
                      <div className='bg-light pt-3 mb-3'>
                        {
                          key === "1" ?
                            <div className='position-relative bg-white'>
                              <div className='row p-3'>
                                <div className='col-xl-8 d-flex align-items-center'>
                                  <h5 className='mb-0'>Activities</h5>
                                </div>
                                <div className='col-xl-4'>
                                  <SelectDropdown
                                    searchable={false}
                                    maxHeight={200}
                                    options={[
                                      { name: "Today", value: "Today" },
                                      { name: "Yesterday", value: "Yesterday" },
                                      { name: "This Week", value: "This Week" },
                                      { name: "Last Week", value: "Last Week" },
                                      { name: "Last 7 Days", value: "Last 7 Days" },
                                      { name: "This Month", value: "This Month" },
                                      { name: "Previous Month", value: "Previous Month" },
                                      { name: "Current Fiscal Year", value: "Current Fiscal Year" },
                                      { name: "Previous Fiscal Year", value: "Previous Fiscal Year" },
                                      { name: "Last 365 Days", value: "Last 365 Days" },
                                      { name: "Custom Date Range", value: "Custom Date Range" }
                                    ]}
                                    labelField="name"
                                    valueField="value"
                                    placeholder="Sort by Date"
                                    selectedValue={null}
                                    onSelect={(value) => null}
                                    menuClass="shadow"
                                  />
                                </div>
                              </div>
                              {
                                activities && activities.length > 0 ?
                                  Object.entries(grouped).map(([dateLabel, dataItems]) => {
                                    return (
                                      <div key={dateLabel} className='border-top'>
                                        <div className='px-3 mt-3 d-flex'>
                                          <div className='alert alert-info border-0 d-flex align-items-center fw-normal px-2 py-1'>
                                            <IoCalendarOutline size={12} />
                                            <small className='small ms-2'>{dateLabel}</small>
                                          </div>
                                        </div>
                                        {
                                          dataItems && dataItems.reverse().map((item, inx) => {
                                            return (
                                              <div key={inx} className='mx-3 border rounded mb-3'>
                                                <div className='p-3 d-flex align-items-start'>
                                                  <div className={item.type === "calls" ? 'activity-icon bg-success' : 'activity-icon bg-info'}>
                                                    {
                                                      item.type === "notes" ?
                                                        <IoDocumentTextOutline size={20} color='white' />
                                                        : item.type === "calls" ?
                                                          <IoCallOutline size={20} color='white' />
                                                          :
                                                          <IoMailOutline size={20} color='white' />
                                                    }
                                                  </div>
                                                  <div className='activity-content'>
                                                    {
                                                      item.type === "calls" ?
                                                        <h6 className='mb-3 fw-600'>
                                                          Logged a call by
                                                          {
                                                            user?.uid === item?.createdBy ?
                                                              <span className='text-primary fw-600 ms-1'>You</span>
                                                              :
                                                              <span className='fw-light'>{businessUsers && businessUsers.find(x => x.id === item.createdBy)?.name || "-"}</span>
                                                          }
                                                        </h6>
                                                        :
                                                        <h6 className='mb-1 fw-600'>{item.title}</h6>
                                                    }
                                                    <p className='mb-0'>{item.note}</p>
                                                    <div className='d-flex align-items-center justify-content-end text-placeholder w-100 mt-3'>
                                                      <span className='small fw-light'>Created By <span className='fw-normal'>{businessUsers && businessUsers.find(x => x.id === item.createdBy)?.name || "-"}</span></span>
                                                      <span className='small px-2'>|</span>
                                                      <span className='small'>{format(new Date(item.createdAt.seconds * 1000), "HH.mm a")}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          })
                                        }
                                      </div>
                                    )
                                  })
                                  :
                                  <div className='py-5 border-top'>
                                    <div className='py-5 d-flex flex-column align-items-center justify-content-center'>
                                      <Image src={noActivityFound} width={80} height={80} className='img-fluid op-5' />
                                      <h5 className='text-center fw-normal mt-3 text-secondary'>No Activity Found!</h5>
                                    </div>
                                  </div>
                              }
                            </div>
                            : key === "2" ?
                              <div className='position-relative bg-white'>
                                <div className='row p-3'>
                                  <div className='col-xl-5 col-lg-5 d-flex align-items-center'>
                                    <h5 className='mb-0'>Notes</h5>
                                  </div>
                                  <div className='col-xl-7 col-lg-7'>
                                    <div className='d-flex'>
                                      <SelectDropdown
                                        searchable={false}
                                        maxHeight={200}
                                        options={[
                                          { name: "Today", value: "Today" },
                                          { name: "Yesterday", value: "Yesterday" },
                                          { name: "This Week", value: "This Week" },
                                          { name: "Last Week", value: "Last Week" },
                                          { name: "Last 7 Days", value: "Last 7 Days" },
                                          { name: "This Month", value: "This Month" },
                                          { name: "Previous Month", value: "Previous Month" },
                                          { name: "Current Fiscal Year", value: "Current Fiscal Year" },
                                          { name: "Previous Fiscal Year", value: "Previous Fiscal Year" },
                                          { name: "Last 365 Days", value: "Last 365 Days" },
                                          { name: "Custom Date Range", value: "Custom Date Range" }
                                        ]}
                                        labelField="name"
                                        valueField="value"
                                        placeholder="Sort by Date"
                                        selectedValue={null}
                                        onSelect={(value) => null}
                                        menuClass="shadow"
                                        containerClass="w-100"
                                      />
                                      <Button
                                        variant='light'
                                        className='text-primary border btn-lg-width fw-600 px-2 py-2 ms-3'
                                        onClick={() => setShowAddNoteModal(true)}
                                      >
                                        <IoMdAdd size={18} />
                                        <div className='ms-1'>Add Note</div>
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                {
                                  activities && activities.filter(ft => ft.type === "notes").length > 0 ?
                                    <div className='border-top'>
                                      {
                                        activities && activities.filter(ft => ft.type === "notes").map((item, index) => {
                                          return (
                                            <div key={index} className='mx-3 border rounded p-3 mt-3'>
                                              <div className='d-flex align-items-center justify-content-between'>
                                                <div className='d-flex align-items-center justify-content-start'>
                                                  <div className='activity-icon bg-info'>
                                                    <IoDocumentTextOutline size={20} color='white' />
                                                  </div>
                                                  <div className='ms-3'>
                                                    <h6 className='mb-0'>{businessUsers && businessUsers.find(x => x.id === item.createdBy)?.name || "-"}</h6>
                                                    <p className='mb-0 small text-placeholder'>{item?.createdAt ? format(new Date(item.createdAt.seconds * 1000), "d MMM yyyy, h:mm a") : "-"}</p>
                                                  </div>
                                                </div>
                                                <Dropdown>
                                                  <Dropdown.Toggle
                                                    variant='light'
                                                    className='p-1 m-0 rounded-5'
                                                  >
                                                    <div className='d-flex align-items-center justify-content-center'>
                                                      <IoMdMore size={20} />
                                                    </div>
                                                  </Dropdown.Toggle>

                                                  <Dropdown.Menu className='p-0 border shadow-sm overflow-hidden'>
                                                    <Dropdown.Item
                                                      onClick={() => {
                                                        setDeleteID(item.id);
                                                        handleShowDelete();
                                                      }}
                                                      className='d-flex align-items-center text-danger p-1'
                                                    >
                                                      <IoTrashOutline size={16} />
                                                      <div className='ms-2'>
                                                        Delete
                                                      </div>
                                                    </Dropdown.Item>
                                                  </Dropdown.Menu>
                                                </Dropdown>
                                              </div>
                                              <div className='mt-4'>
                                                <h6 className='mb-0 fw-600'>{item.title}</h6>
                                                <p className='mb-0 fw-light'>{item.note}</p>
                                              </div>

                                              <NoteComment leadID={leadID} activityID={item.id} selectedActivity={selectedActivity} setSelectedActivity={setSelectedActivity} />

                                              {
                                                selectedActivity === item.id ?
                                                  null
                                                  :
                                                  <div className='mt-3 d-flex align-items-center justify-content-end'>
                                                    <Button
                                                      variant='white'
                                                      className='text-primary border-0 px-0 py-0 fw-600'
                                                      onClick={() => setSelectedActivity(item.id)}
                                                    >
                                                      <small className='d-flex'>
                                                        <IoMdAdd size={16} />
                                                        <span className='ms-1'>Add Comment</span>
                                                      </small>
                                                    </Button>
                                                  </div>
                                              }
                                            </div>
                                          )
                                        })
                                      }
                                    </div>
                                    :
                                    <div className='py-5 border-top'>
                                      <div className='py-5 d-flex flex-column align-items-center justify-content-center'>
                                        <Image src={noActivityFound} width={80} height={80} className='img-fluid op-5' />
                                        <h5 className='text-center fw-normal mt-3 text-secondary'>No Activity Found!</h5>
                                      </div>
                                    </div>
                                }
                                <AddNote leadID={leadID} showModal={showAddNoteModal} setShowModal={setShowAddNoteModal} />
                              </div>
                              : key === "3" ?
                                <div className='position-relative bg-white'>
                                  <div className='row p-3'>
                                    <div className='col-xl-8 d-flex align-items-center'>
                                      <h5 className='mb-0'>Calls</h5>
                                    </div>
                                    <div className='col-xl-4 d-flex align-items-center justify-content-end'>
                                      <Button
                                        variant='light'
                                        className='text-primary border btn-sm-width fw-600 px-2 py-2 ms-3'
                                        onClick={() => setShowAddCallModal(true)}
                                      >
                                        <IoMdAdd size={18} />
                                        <div className='ms-1'>Add Call</div>
                                      </Button>
                                    </div>
                                  </div>
                                  {
                                    activities && activities.filter(ft => ft.type === "calls").length > 0 ?
                                      <div className='border-top'>
                                        {
                                          activities && activities.filter(ft => ft.type === "calls").map((item, index) => {
                                            return (
                                              <div key={index} className='mx-3 border rounded my-3'>
                                                <div className='p-3 d-flex align-items-start'>
                                                  <div className="calls-icon bg-success text-white">
                                                    <IoCallOutline size={20} color='white' />
                                                  </div>
                                                  <div className='calls-content'>
                                                    <div className='d-flex align-items-center justify-content-between'>
                                                      <h6 className='fw-600'>Logged a call</h6>
                                                      <div className='d-flex align-items-center justify-content-end'>
                                                        <Button
                                                          variant='light'
                                                          className='py-1 border rounded-5 text-info border-info'
                                                        >
                                                          <small>{item.callStatus}</small>
                                                        </Button>
                                                        <Dropdown className='ms-3'>
                                                          <Dropdown.Toggle
                                                            variant='light'
                                                            className='p-1 m-0 rounded-5'
                                                          >
                                                            <div className='d-flex align-items-center justify-content-center'>
                                                              <IoMdMore size={20} />
                                                            </div>
                                                          </Dropdown.Toggle>

                                                          <Dropdown.Menu className='p-0 border shadow-sm overflow-hidden'>
                                                            <Dropdown.Item
                                                              onClick={() => {
                                                                setDeleteID(item.id);
                                                                handleShowDelete();
                                                              }}
                                                              className='d-flex align-items-center text-danger p-1'
                                                            >
                                                              <IoTrashOutline size={16} />
                                                              <div className='ms-2'>
                                                                Delete
                                                              </div>
                                                            </Dropdown.Item>
                                                          </Dropdown.Menu>
                                                        </Dropdown>
                                                      </div>
                                                    </div>
                                                    <p className='mt-3'>{item.note}</p>
                                                    <div className='d-flex align-items-center justify-content-end text-placeholder w-100'>
                                                      <BsClock size={12} />
                                                      <small className='small ms-2'>{new Date().toDateString()}</small>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          })
                                        }
                                        <AddCall leadID={leadID} showModal={showAddCallModal} setShowModal={setShowAddCallModal} />
                                      </div>
                                      :
                                      <div className='py-5 border-top'>
                                        <div className='py-5 d-flex flex-column align-items-center justify-content-center'>
                                          <Image src={noActivityFound} width={80} height={80} className='img-fluid op-5' />
                                          <h5 className='text-center fw-normal mt-3 text-secondary'>No Call Found!</h5>
                                        </div>
                                      </div>
                                  }
                                </div>
                                :
                                <div className='position-relative bg-white'>
                                  <div className='row p-3'>
                                    <div className='col-xl-8 d-flex align-items-center'>
                                      <h5 className='mb-0'>Email</h5>
                                    </div>
                                    <div className='col-xl-4 d-flex align-items-center justify-content-end'>
                                      <Button
                                        variant='light'
                                        className='text-primary border btn-width fw-600 px-2 py-2 ms-3'
                                      >
                                        <IoMdAdd size={18} />
                                        <div className='ms-1'>Create Email</div>
                                      </Button>
                                    </div>
                                  </div>
                                  <div className='border-top'>
                                    {/* {
                                      activities && activities.filter(obj => obj.type === "email").map((item, index) => {
                                        return (
                                          <div className='row border'>
                                            <div className='col-xl-4'>
                                              <div className='d-flex align-items-center'>
                                                <div className='email-icon bg-info'>
                                                  AA
                                                </div>
                                              </div>
                                            </div>
                                            <div className='col-xl-6'>
                                            </div>
                                            <div className='col-xl-2'>
                                            </div>
                                          </div>
                                        )
                                      })
                                    } */}

                                    <div className='row border rounded px-1 py-3 m-3 cursor-pointer'>
                                      <div className='col-xl-3'>
                                        <div className='d-flex align-items-center'>
                                          <h6 className='mb-0'>[Subject]</h6>
                                        </div>
                                      </div>
                                      <div className='col-xl-7'>
                                        <h6 className='mb-0'>[Email Content...]</h6>
                                      </div>
                                      <div className='col-xl-2 d-flex align-items-center justify-content-end'>
                                        <p className='mb-0 small text-placeholder'>Apr 10, 2025</p>
                                      </div>
                                    </div>

                                    <div className='col-xl-12 d-flex align-items-center justify-content-center py-5'>
                                      <Button
                                        variant='light'
                                        className='text-primary border btn-width fw-600 px-2 py-2 ms-3'
                                      >
                                        <IoMdAdd size={18} />
                                        <div className='ms-1'>Create Email</div>
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
            }
          </div>
        </div>
      </div>

      <Modal
        show={showDelete}
        onHide={handleCloseDelete}
        backdrop="static"
        keyboard={false}
        contentClassName='border-0 shadow-sm'
        centered
      >
        <div className='bg-light overflow-hidden rounded'>
          <div className='d-flex align-items-center justify-content-between'>
            <h5 className='mb-0 p-3'>Delete Activity</h5>
            <Button
              variant='white'
              className='rounded border-0 px-3'
              onClick={() => handleCloseDelete()}
            >
              <IoClose size={20} />
            </Button>
          </div>
        </div>
        <div className='border-top px-4 pt-4'>
          <h6 className='mb-0'>Are you sure, you want to delete this activity?</h6>
          <div className='pb-3 pt-2 mb-3'>
            <p className='mb-0 text-muted small'>
              <span className='fw-600 text-decoration-underline'>Warning</span> - Deleting this activity will permanently remove it and delete all associated data.
            </p>
          </div>
        </div>
        <div className='d-flex align-items-center justify-content-end px-3 pb-3'>
          <Button
            variant="light"
            onClick={handleCloseDelete}
            className='border px-5 py-2 me-3'
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className='px-5 py-2 btn-sm-width'
            onClick={() => handleDeleteActivity(deleteID)}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </section >
  );
}

export default LeadDetails;